import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { StoreService } from 'app/ngrx/store.service';
import { Company } from 'app/modules/models/company.type';
import { MatDialog } from '@angular/material/dialog';
import { PartnersService } from 'app/shared/services/partners.service';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { AcepActeur } from 'app/modules/models/acepActeur.type';
import { AddressAutocompleteComponent } from 'app/shared/address-autucomplete/address-autucomplete.component';
import { AddressFeature, FeatureCollection } from 'app/modules/models/stateAdress.type';
import { BuildingService } from 'app/modules/customer-details/building/building.service';
import { debounceTime } from 'rxjs';
import { CreationProjetService } from './creation-projet.service';
import { UploadSummaryService } from './upload-summary/upload-summary.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { formatName, sortByName } from 'app/shared/utils';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import {
  getFileNameValidationMessage,
  validateFileNames,
} from 'app/shared/utils/file-name-validation.util';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageNotification } from 'app/modules/models/messageNotification.type';
import { Subscription } from 'rxjs';
import { BroadcastService } from 'app/broadcast.service';
import { CreatePrepareProjectRequest } from '../projectFinancementBody';
import { ProjectFinancementOperations } from 'app/modules/models/projectFinancement.type';
import { OperationsService } from 'app/shared/services/operations.service';
import { CompanyService } from 'app/shared/services/companyservice.service';
import { RedirectionPopUpComponent } from './redirection-pop-up/redirection-pop-up.component';
import { getActorGuid } from 'app/shared/utils/actor-identifiers.util';
import { DraftProjectFinancementDto } from 'app/modules/models/draftProjectFinancement.type';
import * as XLSX from 'xlsx';
@Component({
  selector: 'app-creation-projet',
  templateUrl: './creation-projet.component.html',
  styleUrls: ['./creation-projet.component.scss']
})
export class CreationProjetComponent implements OnInit, OnDestroy {
  readonly signedCards = [
    'Devis',
    'Autres justificatifs (CDC, Etude de dimensionnement, etc.)'
  ];

  readonly preparationCards = [
    'Devis non signé',
    'Autres justificatifs'
  ];

  readonly afterWorksCards = [
    'Devis',
    'Facture',
    'Attestation sur l\'honneur',
    'Autres justificatifs (CDC, Etude de dimensionnement, etc.)'
  ];

  cards = [
    'Devis',
    'Autres justificatifs (CDC, Etude de dimensionnement, etc.)'
  ];

  creationMode: 'signed' | 'preparation' = 'signed';

  combinedFiles: { files: File[]; card: string }[] = [];
  fileValidationMessages: string[] = [];
  batchForm: FormGroup;
  get draftsArray(): FormArray {
    return this.batchForm.get('drafts') as FormArray;
  }

  get hasBatchDraftAnalysisInProgress(): boolean {
    const drafts = this.batchForm?.get('drafts') as FormArray;
    return drafts?.controls?.some(draft => draft.get('dovaStatus')?.value === 'processing') ?? false;
  }

  get canExportDovaReport(): boolean {
    return this.getExportableDovaDrafts().length > 0 && !this.hasBatchDraftAnalysisInProgress;
  }

  isUploading = false;
  projectStatus = '';

  // Entities
  company: Company;
  project: any;

  // Partners
  partner: any;
  selectedPartnerByMandate: any;
  selectedMandataire: any;
  partnersList: AcepActeur[];
  partnersCtrl = new FormControl();

  // Project form
  projectForm: FormGroup;

  // Map-related data
  options: FeatureCollection;
  filteredOptions: FeatureCollection;
  rawItems: any[] = [];
  private documentDraftProcessedSubscription?: Subscription;
  private documentDraftProgressSubscription?: Subscription;
  genericOperations: any[] = [];
  operationSearchFilter = '';
  selectedOperations: any[] = [];
  readonly allowedActivities = [
    'Agriculture',
    'Résidentiel',
    'Tertiaire',
    'Industrie',
    'Réseaux',
    'Transports'
  ];
  groupedOperations: { activityName: string; operations: any[] }[] = [];
  showCoupDePouce = true;
  private snackbarQueue: { msg: string, panelClass: string, duration: number }[] = [];
  private isSnackbarShowing = false;

  constructor(
    private readonly _storeService: StoreService,
    private readonly _partnerService: PartnersService,
    private readonly _dialog: MatDialog,
    private readonly _fb: FormBuilder,
    private readonly _buildingService: BuildingService,
    private readonly _creationProjetService: CreationProjetService,
    private readonly _uploadSummaryService: UploadSummaryService,
    private readonly _snackBar: MatSnackBar,
    private readonly _fuseConfirmationService: FuseConfirmationService,
    private readonly _route: ActivatedRoute,
    private readonly _router: Router,
    private readonly _broadcastService: BroadcastService,
    private readonly _operationsService: OperationsService,
    private readonly _companyService: CompanyService,
  ) {
    this.projectForm = this._fb.group({
      rs: [''],
      nom: [''],
      prenom: [''],
      email: [''],
      tel: [''],
      dateEngagement: [''],
      dateTravauxPrevisionnelle: ['', Validators.required],
      adresse: [''],
      cp: [''],
      ville: [''],
      genericOperationId: [''],
      complement: [''],
      siret: ['']
    });

    this.batchForm = this._fb.group({
      drafts: this._fb.array([])
    });
  }

  onChangeOperation(event: any): void {
    // when user selects an operation, add it to the selectedOperations list (no duplicates)
    try {
      const selectedId = event?.value;
      if (!selectedId) return;
      const op = (this.genericOperations || []).find((g: any) => g.id === selectedId || g._code === selectedId || g.code === selectedId);
      if (op) {
        const exists = this.selectedOperations.some(s => (s.id || s._code || s.code) === (op.id || op._code || op.code));
        if (!exists) this.selectedOperations.push(op);
      }
      // reset the select so user can add more
      this.projectForm.get('genericOperationId')?.setValue(null);
      this.operationSearchFilter = '';
    } catch { }
  }

  removeSelectedOperation(index: number): void {
    if (index >= 0 && index < this.selectedOperations.length) {
      this.selectedOperations.splice(index, 1);
    }
  }

  @ViewChild(MatAutocompleteTrigger) autoTrigger?: MatAutocompleteTrigger;

  openAutocomplete(): void {
    try {
      this.autoTrigger?.openPanel();
    } catch { }
  }

  onOperationChosen(op: any): void {
    if (!op) return;
    const exists = this.selectedOperations.some(s => (s.id || s._code || s.code) === (op.id || op._code || op.code));
    if (!exists) this.selectedOperations.push(op);
    this.operationSearchFilter = '';
  }

  displayOperation = (op: any) => {
    // Keep the input empty after selecting an operation to avoid showing [object Object]
    return '';
  }

  onOperationSearchChange(value: string): void {
    this.operationSearchFilter = value ?? '';
    // Open the autocomplete panel while typing
    try {
      this.autoTrigger?.openPanel();
    } catch { }
  }

  filteredOperationsForGroup(group: { activityName: string; operations: any[] }): any[] {
    const filter = (this.operationSearchFilter || '').toLowerCase().trim();
    return (group.operations || []).filter(op => {
      // determine if operation has any ceeOperationFiles flagged as isCdp
      const hasCdp = Array.isArray(op?.ceeOperationFiles) && op.ceeOperationFiles.some((f: any) => !!f.isCdp);
      if (this.showCoupDePouce && !hasCdp) return false;

      if (!filter) return true;

      // match against label or any ceeOperationFiles name/label
      if ((op.label || '').toLowerCase().includes(filter)) return true;
      if (Array.isArray(op.ceeOperationFiles)) {
        for (const f of op.ceeOperationFiles) {
          if ((f.name || '').toLowerCase().includes(filter)) return true;
          if ((f.label || '').toLowerCase().includes(filter)) return true;
        }
      }
      return false;
    });
  }


  ngOnInit(): void {
    const mode = this._route.snapshot.queryParamMap.get('mode');
    this.projectStatus = (history.state?.projectStatus || this._route.snapshot.queryParamMap.get('projectStatus') || '').toString();
    this.creationMode = mode == 'preparation' ? 'preparation' : 'signed';
    this.company = this._storeService.getCurrentCompany();
    this.partner = this._storeService.getUserPartner();

    this.selectedMandataire = localStorage.getItem('currentMandataire_localstorage') ? JSON.parse(localStorage.getItem('currentMandataire_localstorage')) : null;
    this.selectedPartnerByMandate = localStorage.getItem('currentPartner_localstorage') ? JSON.parse(localStorage.getItem('currentPartner_localstorage')) : null;
    this.configureDocumentCards();

    const selectedCompanyContext = this.selectedPartnerByMandate || this.partner || this.selectedMandataire;
    if (selectedCompanyContext) {
      this.loadWorkflowCompany(selectedCompanyContext);
    } else if (
      this.company?.isAvantTravauxEnabled === undefined ||
      this.company?.isCssIndEnabled === undefined ||
      this.company?.hasEProjetAccess === undefined
    ) {
      this.loadWorkflowCompany(this.partner);
    }

    if (this.selectedMandataire) {
      this.getPartnersByMandate()
    }
    this.getProjectHistory();
    this.initDovaSubscriptions();

    this.projectForm.get('dateEngagement')?.valueChanges.subscribe(() => {
      this.selectedOperations = [];
      this.loadGenericOperations();
    });
    // Load available generic operations for selection
    this.loadGenericOperations();
  }

  ngOnDestroy(): void {
    this.documentDraftProcessedSubscription?.unsubscribe();
    this.documentDraftProgressSubscription?.unsubscribe();
  }

  private handleDocumentDraftProgressNotification(notification: MessageNotification): void {
    try {
      const content = notification?.content ? JSON.parse(notification.content) : null;
      if (!content) return;

      const projectId = content?.ProjectId || content?.projectId || null;
      const message = content?.message ?? content?.Message ?? null;
      const progress = Number(content?.progress ?? content?.Progress ?? null);

      const batchDraft = this.getBatchDraftGroupByProjectId(projectId);
      if (batchDraft) {
        if (batchDraft.get('dovaStatus')?.value === 'completed') {
          return;
        }

        if (!isNaN(progress)) {
          const progressPercent = Math.max(0, Math.min(100, Math.round(progress)));
          batchDraft.patchValue({
            dovaProgressPercent: progressPercent,
            dovaProgressMessage: message ? String(message) : null,
            dovaStatus: 'processing',
          }, { emitEvent: false });
        }
        return;
      }

    } catch (e) {
      console.error('Failed to parse draft progress notification', e, notification?.content);
    }
  }

  getProjectHistory(): void {
    var partnerId = this.partner && this.partner?.guidId ? this.partner?.guidId : (this.partner?.id ? this.partner?.id : this.selectedPartnerByMandate?.id);
    var mandateId = this.selectedMandataire ? this.selectedMandataire.id : null;
    this._partnerService.getProjectHistory(partnerId, mandateId).subscribe({
      next: res => {
        this.rawItems = res;
      },
      error: _ => {
        this._snackBar.open('Une erreur serveur est survenue lors du chargement de l\'historique des projets !', null, {
          duration: 2000,
          panelClass: 'warn-snackbar'
        });
      }
    });
  }

  onViewFile(card: any): void {
    if (card.file) {
      const fileURL = URL.createObjectURL(card.file);
      window.open(fileURL, '_blank');
    } else {
      alert(`Aucun fichier sélectionné pour "${card.label}".`);
    }
  }

  onFileDeleted(event: { file: File, card: string }) {
    const { file, card } = event;
    const index = this.combinedFiles.findIndex(f => f.card === card);

    if (index > -1) {
      this.combinedFiles[index].files = this.combinedFiles[index].files.filter(f => f !== file);

      // If no files remain, remove the entry entirely
      if (this.combinedFiles[index].files.length === 0) {
        this.combinedFiles.splice(index, 1);
      }

      this.refreshFileValidationState();
    }
  }


  onFileSelected(event: { files: File[], card: string }) {
    const { files, card } = event;
    const isMultiFile = card === 'Autres justificatifs (CDC, Etude de dimensionnement, etc.)' || card === 'Autres justificatifs';
    const currentSelectionNames = this.combinedFiles.flatMap(entry => entry.files.map(file => file.name));
    const validFiles = files.filter(file => !getFileNameValidationMessage(file.name, {}, currentSelectionNames));
    const invalidFileNames = files
      .filter(file => !!getFileNameValidationMessage(file.name, {}, currentSelectionNames))
      .map(file => file.name);

    if (invalidFileNames.length > 0) {
      this._snackBar.open(
        `Nom(s) de fichier invalide(s) : ${invalidFileNames.join(', ')}`,
        null,
        {
          duration: 3500,
          panelClass: 'warn-snackbar'
        }
      );
    }

    if (validFiles.length === 0) {
      this.refreshFileValidationState();
      return;
    }

    const index = this.combinedFiles.findIndex(f => f.card === card);

    if (isMultiFile) {
      if (index > -1) {
        // Avoid duplicates by checking file names
        const existing = this.combinedFiles[index].files.map(f => f.name);
        const newFiles = validFiles.filter(f => !existing.includes(f.name));
        this.combinedFiles[index].files.push(...newFiles);
      } else {
        this.combinedFiles.push({ card, files: [...validFiles] });
      }
    } else {
      // For single-file cards, always replace with the first file
      if (index > -1) {
        this.combinedFiles[index].files = [validFiles[0]];
      } else {
        this.combinedFiles.push({ card, files: [validFiles[0]] });
      }
    }

    this.refreshFileValidationState();
  }



  deposeProjectFinancement() {
    if (!this.isValidForSubmission()) {
      return;
    }

    this.isUploading = true;
    const formData = this.buildProjectFilesFormData();
    if (!formData) {
      this.isUploading = false;
      return;
    }

    const submissionContext = this.getSubmissionContext();
    if (!submissionContext) {
      this.isUploading = false;
      this._snackBar.open('Impossible de récupérer le contexte partenaire pour le dépôt.', null, {
        duration: 2500,
        panelClass: 'warn-snackbar'
      });
      return;
    }

    if (this.isCssIndWithoutEspaceProjet && !this.isPreparationMode) {
      this.createCssIndAfterWorksProject(formData, submissionContext);
      return;
    }

    this.createClassicProject(formData, submissionContext);
  }

  /**
   * Returns the tooltip text for the submit button depending on form and upload state.
   */
  getSubmitTooltip(): string {
    if (!this.isPreparationMode && this.projectForm.get('dateEngagement')?.invalid) {
      return 'Veuillez remplir la date d\'engagement';
    }

    if (this.requiresPlannedEndDate && this.projectForm.invalid) {
      return 'Veuillez remplir la date de fin des travaux';
    }

    if (this.requiresManualOperationSelection && this.selectedOperations.length === 0) {
      return 'Veuillez sélectionner au moins une opération';
    }

    if (this.isUploadDisabled()) {
      return this.isAfterWorksOnly
        ? 'Veuillez ajouter le devis, la facture et l\'attestation sur l\'honneur'
        : 'Veuillez ajouter au moins un devis';
    }

    if (this.isUploading) {
      return 'Votre projet est en cours de dépôt, veuillez patienter...';
    }

    if (this.selectedMandataire && !this.selectedPartnerByMandate) {
      return 'Veuillez sélectionner un partenaire pour ce mandataire';
    }

    return '';
  }


  getSingleFileCard(): { files: File[], card: string }[] {
    const cardOrder = ['Devis', 'Devis non signé', 'Facture', 'Attestation sur l\'honneur'];
    return this.combinedFiles
      .filter(f => !['Autres justificatifs (CDC, Etude de dimensionnement, etc.)', 'Autres justificatifs'].includes(f.card))
      .sort((a, b) => {
        const indexA = cardOrder.indexOf(a.card);
        const indexB = cardOrder.indexOf(b.card);
        return indexA - indexB;
      });

  }

  getMultipleFileCard(): { files: File[], card: string }[] {
    return this.combinedFiles.filter(f => ['Autres justificatifs (CDC, Etude de dimensionnement, etc.)', 'Autres justificatifs'].includes(f.card));
  }

  isUploadDisabled(): boolean {
    // In preparation mode, require that a 'Devis non signé' file is present.
    if (this.isPreparationMode) {
      const requiredCardsPrep = ['Devis non signé'];
      const missingRequiredFilesPrep = requiredCardsPrep.some(requiredCard => {
        const found = this.combinedFiles.find(f => f.card === requiredCard);
        return !found || found.files.length === 0;
      });

      return missingRequiredFilesPrep || this.hasFileValidationErrors();
    }

    const requiredCards = this.isAfterWorksOnly
      ? ['Devis', 'Facture', 'Attestation sur l\'honneur']
      : ['Devis'];

    const missingRequiredFiles = requiredCards.some(requiredCard => {
      const found = this.combinedFiles.find(f => f.card === requiredCard);
      return !found || found.files.length === 0;
    });

    return missingRequiredFiles || this.hasFileValidationErrors();
  }

  hasFileValidationErrors(): boolean {
    return this.fileValidationMessages.length > 0;
  }

  private buildProjectFilesFormData(): FormData | null {
    const formData = new FormData();
    const addedFileNames = new Set<string>();
    let otherFilesIndex = 0;

    for (const entry of this.combinedFiles) {
      const { card, files } = entry;

      for (const file of files) {
        const nameWithoutExtension = file.name.split('.').slice(0, -1).join('.') || file.name;

        if (addedFileNames.has(nameWithoutExtension)) {
          this._fuseConfirmationService.open({
            title: 'Nom de fichier en double',
            message: `Le fichier "${nameWithoutExtension}" a déjà été ajouté. Veuillez renommer le fichier pour éviter les doublons.`,
          });
          return null;
        }

        addedFileNames.add(nameWithoutExtension);

        const name = file.name;
        const contentType = file.type;
        const extension = name.split('.').pop() || '';

        switch (card) {
          case 'Devis':
          case 'Devis non signé':
            formData.append('DevisFile.BlobFile', file);
            formData.append('DevisFile.Name', name);
            formData.append('DevisFile.ContentType', contentType);
            formData.append('DevisFile.Extension', extension);
            break;
          case 'Facture':
            formData.append('InvoiceFile.BlobFile', file);
            formData.append('InvoiceFile.Name', name);
            formData.append('InvoiceFile.ContentType', contentType);
            formData.append('InvoiceFile.Extension', extension);
            break;
          case 'Attestation sur l\'honneur':
            formData.append('AhFile.BlobFile', file);
            formData.append('AhFile.Name', name);
            formData.append('AhFile.ContentType', contentType);
            formData.append('AhFile.Extension', extension);
            break;
          case 'Autres justificatifs (CDC, Etude de dimensionnement, etc.)':
          case 'Autres justificatifs':
            formData.append(`OtherFiles[${otherFilesIndex}].BlobFile`, file);
            formData.append(`OtherFiles[${otherFilesIndex}].Name`, name);
            formData.append(`OtherFiles[${otherFilesIndex}].ContentType`, contentType);
            formData.append(`OtherFiles[${otherFilesIndex}].Extension`, extension);
            otherFilesIndex++;
            break;
        }
      }
    }

    return formData;
  }

  private handleDocumentDraftProcessedNotification(notification: MessageNotification): void {
    let parsedContent: any = null;
    try {
      parsedContent = notification?.content ? JSON.parse(notification.content) : null;
    } catch {
      return;
    }

    const batchDraft = this.getBatchDraftGroupByProjectId(parsedContent?.projectId);
    if (batchDraft) {
      this.saveBatchDraftDovaAnalysis(batchDraft, parsedContent);
    }
  }

  private saveBatchDraftDovaAnalysis(draftGroup: FormGroup, dovaResponse: any): void {
    const projectId = draftGroup.get('projectId')?.value;
    const dovaAnalysisJson = JSON.stringify(dovaResponse);
    const missingCount = this.countDovaMissingFields(dovaResponse);

    draftGroup.patchValue({
      dovaStatus: 'completed',
      dovaProgressPercent: 100,
      dovaProgressMessage: 'Analyse terminée.',
      dovaAnalysis: dovaAnalysisJson,
      dovaMissingCount: missingCount,
    }, { emitEvent: false });

    this._creationProjetService.saveBatchDovaAnalysis(projectId, dovaAnalysisJson).subscribe({
      next: () => {
        this._snackBar.open('Contrôle IA terminé et enregistré.', 'Fermer', {
          duration: 2500,
          panelClass: 'accent-snackbar'
        });
      },
      error: () => {
        draftGroup.patchValue({
          dovaStatus: 'error',
          dovaProgressMessage: 'Analyse terminée, mais l\'enregistrement a échoué.',
        }, { emitEvent: false });
        this._snackBar.open('Erreur lors de l\'enregistrement du contrôle IA.', 'Fermer', {
          duration: 3000,
          panelClass: 'warn-snackbar'
        });
      }
    });
  }

  private extractMissingFieldsFromDovaResponse(dovaResponse: any): { label: string; status: string }[] {
    const missingFields: { label: string; status: string }[] = [];

    for (const dovaFile of dovaResponse?.files || []) {
      for (const doc of dovaFile?.logicalDocuments || []) {
        for (const field of doc?.fields || []) {
          const isBooleanType = field.type?.toLowerCase() === 'boolean';
          const isMissing = field.value === null
            || field.value === undefined
            || field.value === ''
            || (isBooleanType && (field.value === false || field.value?.toLowerCase() === 'false'));

          if (isMissing) {
            missingFields.push({ label: field.label, status: 'Manquant' });
          }
        }
      }
    }

    return missingFields;
  }

  private countDovaMissingFields(dovaResponse: any): number {
    return this.extractMissingFieldsFromDovaResponse(dovaResponse).length;
  }

  private parseDraftDovaAnalysis(dovaAnalysis: any): any {
    if (!dovaAnalysis) return null;

    try {
      const parsed = typeof dovaAnalysis === 'string' ? JSON.parse(dovaAnalysis) : dovaAnalysis;
      return typeof parsed === 'string' ? JSON.parse(parsed) : parsed;
    } catch {
      return null;
    }
  }

  getDovaReportExportTooltip(): string {
    if (this.hasBatchDraftAnalysisInProgress) {
      return 'Une analyse IA est encore en cours.';
    }

    if (this.getExportableDovaDrafts().length === 0) {
      return 'Aucun devis vérifié à exporter.';
    }

    return 'Exporter le rapport des contrôles IA.';
  }

  exportDovaReport(): void {
    if (!this.canExportDovaReport) {
      return;
    }

    const rows = this.buildDovaReportRows();
    const worksheet = XLSX.utils.json_to_sheet(rows);
    worksheet['!cols'] = [
      { wch: 40 },
      { wch: 18 },
      { wch: 20 },
      { wch: 80 },
    ];
    worksheet['!rows'] = [
      { hpt: 22 },
      ...rows.map(row => ({
        hpt: Math.max(22, String(row['Détail des anomalies'] || '').split('\n').length * 18),
      })),
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Rapport IA');
    XLSX.writeFile(workbook, `rapport-controle-ia_${this.getTodayFilePostfix()}.xlsx`);
  }

  private getExportableDovaDrafts(): FormGroup[] {
    return this.draftsArray.controls.filter(control => {
      const status = control.get('dovaStatus')?.value;
      return status === 'completed' || status === 'error';
    }) as FormGroup[];
  }

  private buildDovaReportRows(): any[] {
    return this.getExportableDovaDrafts().map(draft => {
      const status = draft.get('dovaStatus')?.value;
      const dovaResponse = this.parseDraftDovaAnalysis(draft.get('dovaAnalysis')?.value);
      const missingFields = status === 'completed' ? this.extractMissingFieldsFromDovaResponse(dovaResponse) : [];
      const missingCount = missingFields.length;
      const reportStatus = status === 'error' ? 'Erreur' : missingCount === 0 ? 'Conforme' : 'Non conforme';
      const reportDetails = this.getDovaReportDetails(status, missingFields);

      return {
        Fichier: draft.get('fileName')?.value || '',
        'Statut IA': reportStatus,
        'Nombre d\'anomalies': status === 'completed' ? missingCount : '',
        'Détail des anomalies': reportDetails,
      };
    });
  }

  private getDovaReportDetails(status: string, missingFields: { label: string; status: string }[]): string {
    if (status === 'error') return 'Analyse en erreur';
    if (missingFields.length === 0) return 'Aucune anomalie';

    return missingFields.map(field => field.label).filter(Boolean).join('\n');
  }

  private getTodayFilePostfix(): string {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  private getSubmissionContext(): any | null {
    const company = this.company;
    const connectedUser = this._storeService.getConnectedUser();
    let partnerId = this.partner && this.partner?.guidId ? this.partner?.guidId : (this.partner?.id ? this.partner?.id : this.selectedPartnerByMandate?.id);
    const mandateId = this.selectedMandataire ? this.selectedMandataire.id : null;
    let partnerName = this.selectedPartnerByMandate ? this.selectedPartnerByMandate.name : this.partner?.nom;
    const mandateName = this.selectedMandataire ? this.selectedMandataire.name : null;
    let siretPartner = this.selectedPartnerByMandate ? this.selectedPartnerByMandate.siret : this.partner?.siret;
    const userId = connectedUser?.guidId || connectedUser?.id || connectedUser?.userId || null;

    if (this.partnersCtrl?.value) {
      partnerId = this.partnersCtrl.value.id;
      partnerName = this.partnersCtrl.value.name;
      siretPartner = this.partnersCtrl.value.siret;
    }

    if (!company?.id || !partnerId) {
      return null;
    }

    return {
      companyId: company.id,
      userId,
      partnerId,
      mandateId,
      partnerName,
      mandateName,
      siretPartner,
      isPreparationMode: this.isPreparationMode,
    };
  }

  private loadGenericOperations(): void {
    if (!this.requiresManualOperationSelection) {
      this.genericOperations = [];
      this.groupedOperations = [];
      return;
    }

    const partnerId = getActorGuid(this.selectedPartnerByMandate) ?? getActorGuid(this.partner);
    const mandataireId = getActorGuid(this.selectedMandataire) ?? '';
    const engagementDate = this.projectForm.get('dateEngagement')?.value;

    if (!partnerId || !engagementDate) {
      this.genericOperations = [];
      this.groupedOperations = [];
      return;
    }

    try {
      const formattedDate = new Date(engagementDate).toISOString();

      this._operationsService.getAuthorizedByContract(partnerId, mandataireId, formattedDate).subscribe({
        next: (result) => {
          this.genericOperations = result;
          this.groupGenericOperations();
        },
        error: () => {
          this.genericOperations = [];
          this.groupedOperations = [];
        }
      });
    } catch {
      this.genericOperations = [];
      this.groupedOperations = [];
    }
  }

  groupGenericOperations() {
    const groups: { [key: string]: any[] } = {};

    for (const op of this.genericOperations) {
      const activityName = op.ceeOperationFiles?.[0]?.activitySector?.name;

      if (!activityName || !this.allowedActivities.includes(activityName)) {
        continue;
      }
      if (!groups[activityName]) {
        groups[activityName] = [];
      }
      groups[activityName].push(op);
    }

    this.groupedOperations = Object.entries(groups).map(([activityName, operations]) => ({
      activityName,
      operations: (operations || []).sort((a, b) => (a?.label ?? '').localeCompare(b?.label ?? '', 'fr', { sensitivity: 'base' })),
    }));

    this.groupedOperations.sort((a, b) =>
      (a?.activityName ?? '').localeCompare(b?.activityName ?? '', 'fr', { sensitivity: 'base' })
    );
  }

  private generateProjectId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }

    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  private refreshFileValidationState(): void {
    const validation = validateFileNames(
      this.combinedFiles.flatMap(entry => entry.files.map(file => file.name))
    );

    this.fileValidationMessages = validation.messages;
  }

  getPartnersByMandate() {
    this._partnerService.getPartnersByMandate(this.selectedMandataire.id).subscribe(partners => {
      this.partnersList = [
        ...new Map(partners.map(p => [p.id, p])).values()
      ];
      this.partnersList = [...this.partnersList.filter(p => p.isActive)];
      this.partnersList = sortByName(this.partnersList);
      if (this.selectedPartnerByMandate) {
        this.partnersCtrl.setValue(this.partnersList.find(p => p.id === this.selectedPartnerByMandate.id));
      }
    });
  }

  onPartnerSelected(event: any) {
    if (event?.value) {
      this.selectedPartnerByMandate = event.value;
      this.loadWorkflowCompany(this.selectedPartnerByMandate);
      this.getProjectHistory();
    }
  }

  showAdressAutocomplete() {
    let dialog = this._dialog.open(AddressAutocompleteComponent, {
      width: '600px',
      data: this.options,
    });

    dialog.afterClosed().subscribe((result: any) => {
      if (result) {
        this.splitAddress(result.option.value);
      }
    });
  }

  subscribeToAddressChanges() {
    this.projectForm.get('address').valueChanges.pipe(debounceTime(1000)).subscribe(value => {
      if (value !== null && value !== undefined && value !== '') {
        this._buildingService.getAddress(value).subscribe((result: FeatureCollection) => {
          this.options = result;
          this.filteredOptions = this.options;
        });
      }
    });
  }

  splitAddress(mainAddress: AddressFeature) {
    if (mainAddress) {
      let zipCode = mainAddress.properties.postcode;
      let name = mainAddress?.properties?.name ? mainAddress.properties.name : "";
      let town: string = mainAddress?.properties?.city || mainAddress.properties.district || "";

      this.projectForm.get('adresse')?.setValue(name);
      this.projectForm.get('ville')?.setValue(town);
      this.projectForm.get('cp')?.setValue(zipCode);
    }
  }

  selectedOption(event: any) {
    const selectedAddress: AddressFeature = event.option.value;
    this.splitAddress(selectedAddress);
  }

  // Method to reset form, files, and partners
  resetAll() {
    // Reset form
    this.projectForm.reset();
    // Clear uploaded files
    this.combinedFiles = [];
    this.selectedOperations = [];
    this.operationSearchFilter = '';
    this.isUploading = false;
  }

  get isPreparationMode(): boolean {
    return this.creationMode === 'preparation';
  }

  get isAfterWorksOnly(): boolean {
    return !this.isPreparationMode &&
      this.company?.isAvantTravauxEnabled === false &&
      this.company?.isCssIndEnabled === true;
  }

  get usesSingleProjectFlow(): boolean {
    return this.isPreparationMode || this.isAfterWorksOnly;
  }

  get requiresManualOperationSelection(): boolean {
    return !this.isPreparationMode && !this.isAfterWorksOnly;
  }

  get requiresPlannedEndDate(): boolean {
    return !this.isAfterWorksOnly;
  }

  private get isCssIndWithoutEspaceProjet(): boolean {
    return this.isAfterWorksOnly;
  }

  private configureDocumentCards(): void {
    this.cards = this.isPreparationMode
      ? this.preparationCards
      : this.isAfterWorksOnly
        ? this.afterWorksCards
        : this.signedCards;
  }

  isRequiredDocument(card: string): boolean {
    return this.isAfterWorksOnly && [
      'Devis',
      'Facture',
      'Attestation sur l\'honneur'
    ].includes(card);
  }

  goToDossiersProjet(): void {
    this._router.navigate(['/partner-dossier-project']);
  }

  private isValidForSubmission(): boolean {
    if (!this.isPreparationMode && this.projectForm.get('dateEngagement')?.invalid) {
      this.projectForm.markAllAsTouched();
      this._snackBar.open('Veuillez renseigner la date d\'engagement.', null, {
        duration: 2000,
        panelClass: 'warn-snackbar'
      });
      return false;
    }

    if (this.requiresPlannedEndDate && this.projectForm.invalid) {
      this.projectForm.markAllAsTouched();
      this._snackBar.open('Veuillez renseigner la date de fin des travaux prévue.', null, {
        duration: 2000,
        panelClass: 'warn-snackbar'
      });
      return false;
    }

    if (this.requiresManualOperationSelection && this.selectedOperations.length === 0) {
      this._snackBar.open('Veuillez sélectionner au moins une opération.', null, {
        duration: 2000,
        panelClass: 'warn-snackbar'
      });
      return false;
    }

    if (this.isUploadDisabled()) {
      this.refreshFileValidationState();
      return false;
    }

    return true;
  }

  private getMappedCeeOperations(): ProjectFinancementOperations[] {
    return this.selectedOperations.map((op, index): ProjectFinancementOperations => {
      let fileId = op.id;
      let fileName = '';

      if (op.ceeOperationFiles && op.ceeOperationFiles.length > 0) {
        const matchingFile = op.ceeOperationFiles.find((file: any) =>
          this.showCoupDePouce ? file.isCdp === true : !file.isCdp
        );

        if (matchingFile) {
          fileId = matchingFile.id;
          fileName = matchingFile.name;
        } else {
          fileId = op.ceeOperationFiles[0].id;
          fileName = op.ceeOperationFiles[0].name;
        }
      }

      const adresse = this.projectForm.get('adresse')?.value || '';
      const complement = this.projectForm.get('complement')?.value || '';
      const cp = this.projectForm.get('cp')?.value || '';
      const ville = this.projectForm.get('ville')?.value || '';

      return {
        genericOperationId: op.id,
        genericOperationName: op.label,
        ceeOperationFileId: fileId,
        ceeOperationFileName: fileName,
        adresse: adresse,
        complement: complement,
        cp: cp,
        ville: ville,
        siteCopro: null,
        cdp: this.showCoupDePouce,
        rank: index + 1,
        ceeVolume: 0,
        classicVolume: 0,
        modesteVolume: 0,
        precaVolume: 0,
        sousTraitantId: null,
        baliseIn: [],
        baliseInToSave: [],
        fieldsUsed: [],
        caseUsedTera: null,
        marqueName: null,
        referenceName: null
      };
    });
  }

  initDovaSubscriptions() {
    // Clear any existing subscriptions to prevent memory leaks
    this.documentDraftProcessedSubscription?.unsubscribe();
    this.documentDraftProgressSubscription?.unsubscribe();

    this.documentDraftProcessedSubscription = this._broadcastService.documentDraftProcessed$.subscribe(notification => {
      this.handleDocumentDraftProcessedNotification(notification);
    });

    this.documentDraftProgressSubscription = this._broadcastService.documentDraftProgress$.subscribe(notification => {
      this.handleDocumentDraftProgressNotification(notification);
    });
  }

  getCompanyBySiret(siret: string, codeClient: string) {
    if (!siret && !codeClient) {
      return;
    }

    this._companyService.getCompanyBySiret(siret, codeClient).subscribe({
      next: (company: Company) => {
        this.company = { ...this.company, ...company };
        this.configureDocumentCards();

        if (!this.usesSingleProjectFlow) {
          this.loadPendingDrafts();
        }
      },
      error: error => {
        console.error('Une erreur serveur est survenue !', error);
      }
    });
  }

  private loadWorkflowCompany(companyContext: any): void {
    const siret = companyContext?.siret;
    const codeClient = companyContext?.codeClient ?? companyContext?.code;

    if (!siret && !codeClient) {
      return;
    }

    // In Espace Partenaire administration, the selected partner/mandataire
    // configuration drives the creation workflow, not the connected admin.
    this.getCompanyBySiret(siret, codeClient);
  }

  onDragOverBatch(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onDropBatch(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer?.files) {
      this.handleBatchFiles(Array.from(event.dataTransfer.files));
    }
  }

  onFileInputChangeBatch(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.handleBatchFiles(Array.from(input.files));
    }
    input.value = ''; // Reset so the same file can be clicked again
  }



  private loadGenericOperationsForRow(engagementDate: any, draftGroup: FormGroup): void {
    const partnerId = getActorGuid(this.selectedPartnerByMandate) ?? getActorGuid(this.partner);
    const mandataireId = getActorGuid(this.selectedMandataire) ?? '';

    if (!partnerId || !engagementDate) return;

    try {
      const formattedDate = new Date(engagementDate).toISOString();

      this._operationsService.getAuthorizedByContract(partnerId, mandataireId, formattedDate).subscribe({
        next: (result) => {
          // Process the raw result into groups and save it strictly to THIS row
          const groups = this.groupGenericOperationsData(result);
          draftGroup.get('availableGroups')?.setValue(groups);
        },
        error: () => {
          draftGroup.get('availableGroups')?.setValue([]);
        }
      });
    } catch {
      draftGroup.get('availableGroups')?.setValue([]);
    }
  }

  private groupGenericOperationsData(operationsList: any[]): any[] {
    const groups: { [key: string]: any[] } = {};

    for (const op of operationsList) {
      const activityName = op.ceeOperationFiles?.[0]?.activitySector?.name;
      if (!activityName || !this.allowedActivities.includes(activityName)) continue;

      if (!groups[activityName]) groups[activityName] = [];
      groups[activityName].push(op);
    }

    const grouped = Object.entries(groups).map(([activityName, ops]) => ({
      activityName,
      operations: (ops || []).sort((a, b) => (a?.label ?? '').localeCompare(b?.label ?? '', 'fr', { sensitivity: 'base' })),
    }));

    return grouped.sort((a, b) => (a?.activityName ?? '').localeCompare(b?.activityName ?? '', 'fr', { sensitivity: 'base' }));
  }


  filteredOperationsForGroupBatch(group: { activityName: string; operations: any[] }, searchFilter: string, showCdp: boolean): any[] {
    const filter = (searchFilter || '').toLowerCase().trim();
    return (group.operations || []).filter(op => {
      const hasCdp = Array.isArray(op?.ceeOperationFiles) && op.ceeOperationFiles.some((f: any) => !!f.isCdp);
      if (showCdp && !hasCdp) return false;

      if (!filter) return true;

      if ((op.label || '').toLowerCase().includes(filter)) return true;
      if (Array.isArray(op.ceeOperationFiles)) {
        for (const f of op.ceeOperationFiles) {
          if ((f.name || '').toLowerCase().includes(filter)) return true;
          if ((f.label || '').toLowerCase().includes(filter)) return true;
        }
      }
      return false;
    });
  }

  formatBatchBytes(bytes: number, decimals = 2): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024, dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'Ko', 'Mo', 'Go'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  removeBatchDraft(index: number): void {
    this.deleteBatchDraft(index);
  }

  private deleteBatchDraft(index: number): void {
    const draftGroup = this.draftsArray.at(index);
    const projectId = draftGroup.get('projectId')?.value;

    if (projectId) {
      this._creationProjetService.deleteDraftProjects([projectId]).subscribe({
        next: () => {
          this.draftsArray.removeAt(index);
          this._snackBar.open('Brouillon supprimé', 'Fermer', { duration: 2000 });
        },
        error: () => {
          this._snackBar.open('Erreur lors de la suppression du brouillon', 'Fermer', { duration: 3000, panelClass: 'warn-snackbar' });
        }
      });
    } else {
      this.draftsArray.removeAt(index);
    }
  }

  clearAllBatchDrafts(): void {
    if (this.draftsArray.length === 0) return;

    this.openDraftDeletionConfirmation(
      'Vider la liste',
      'Vous vous apprêtez à supprimer tous les brouillons de la liste. Les documents temporaires seront supprimés. Voulez-vous continuer ?',
      () => this.deleteAllBatchDrafts()
    );
  }

  private deleteAllBatchDrafts(): void {
    if (this.draftsArray.length === 0) return;

    const projectIds = this.draftsArray.controls
      .map(ctrl => ctrl.get('projectId')?.value)
      .filter(id => !!id);

    if (projectIds.length > 0) {
      this._creationProjetService.deleteDraftProjects(projectIds).subscribe({
        next: () => {
          this.draftsArray.clear();
          this._snackBar.open('Tous les brouillons ont été supprimés', 'Fermer', { duration: 3000 });
        },
        error: () => {
          this._snackBar.open('Erreur lors de la suppression des brouillons', 'Fermer', { duration: 3000, panelClass: 'warn-snackbar' });
        }
      });
    } else {
      this.draftsArray.clear();
    }
  }

  private openDraftDeletionConfirmation(title: string, message: string, onConfirm: () => void): void {
    const dialogRef = this._fuseConfirmationService.open({
      title,
      message,
      actions: {
        confirm: {
          label: 'Supprimer'
        },
        cancel: {
          label: 'Annuler'
        }
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'confirmed') {
        onConfirm();
      }
    });
  }

  triggerAdditionalFileBatch(index: number): void {
    const fileInput = document.getElementById(`additionalFileInput_${index}`) as HTMLInputElement;
    if (fileInput) fileInput.click();
  }

  onAdditionalFileSelectedBatch(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const draftGroup = this.draftsArray.at(index);
      const currentFiles = draftGroup.get('additionalFiles')?.value || [];
      draftGroup.get('additionalFiles')?.setValue([...currentFiles, ...Array.from(input.files)]);
    }
  }

  removeAdditionalFileBatch(draftIndex: number, fileIndex: number): void {
    const draftGroup = this.draftsArray.at(draftIndex);
    const currentFiles = draftGroup.get('additionalFiles')?.value || [];
    const newFiles = [...currentFiles];
    newFiles.splice(fileIndex, 1);
    draftGroup.get('additionalFiles')?.setValue(newFiles);
  }

  onOperationChosenBatch(op: any, index: number): void {
    if (!op) return;
    const draftGroup = this.draftsArray.at(index);
    const currentOps = draftGroup.get('operations')?.value || [];
    const exists = currentOps.some((s: any) => (s.id || s._code) === (op.id || op._code));
    if (!exists) draftGroup.get('operations')?.setValue([...currentOps, op]);
    draftGroup.get('operationSearch')?.setValue('');
  }

  /**
 * Checks if at least one selected operation has a CEE file flagged with CDP.
 */
  hasCdpOperationSelected(operations: any[]): boolean {
    if (!Array.isArray(operations) || operations.length === 0) return false;
    return operations.some(op =>
      Array.isArray(op?.ceeOperationFiles) && op.ceeOperationFiles.some((f: any) => !!f.isCdp)
    );
  }

  /**
   * Returns the matching CEE file name depending on the CDP toggle state.
   */
  getSelectedCeeFileName(op: any, showCdp: boolean): string {
    if (!op?.ceeOperationFiles || op.ceeOperationFiles.length === 0) return '';
    const matchingFile = op.ceeOperationFiles.find((file: any) =>
      showCdp ? file.isCdp === true : !file.isCdp
    );
    return matchingFile ? matchingFile.name : op.ceeOperationFiles[0].name;
  }

  removeSelectedOperationBatch(draftIndex: number, opIndex: number): void {
    const draftGroup = this.draftsArray.at(draftIndex);
    const currentOps = [...draftGroup.get('operations')?.value];
    currentOps.splice(opIndex, 1);
    draftGroup.get('operations')?.setValue(currentOps);
  }

  verifyBatchDraftDevis(index: number): void {
    const draftGroup = this.draftsArray.at(index) as FormGroup;
    const projectId = draftGroup.get('projectId')?.value;
    const userId = this.getSubmissionContext()?.userId;

    if (!projectId || !userId) {
      this._snackBar.open('Impossible de lancer le contrôle IA pour ce brouillon.', 'Fermer', {
        duration: 3000,
        panelClass: 'warn-snackbar'
      });
      return;
    }

    draftGroup.patchValue({
      dovaStatus: 'processing',
      dovaProgressPercent: 0,
      dovaProgressMessage: 'Analyse du devis en cours...',
      dovaMissingCount: null,
    }, { emitEvent: false });

    this._creationProjetService.verifyDraftProjectDova(projectId, userId).subscribe({
      next: () => {
        this._snackBar.open('Analyse du devis lancée.', 'Fermer', {
          duration: 2500,
          panelClass: 'accent-snackbar'
        });
      },
      error: () => {
        draftGroup.patchValue({
          dovaStatus: 'error',
          dovaProgressMessage: 'Erreur lors du lancement du contrôle IA.',
        }, { emitEvent: false });
        this._snackBar.open('Erreur lors du lancement du contrôle IA.', 'Fermer', {
          duration: 3000,
          panelClass: 'warn-snackbar'
        });
      }
    });
  }

  getBatchDovaButtonLabel(draft: any): string {
    const status = draft.get('dovaStatus')?.value;
    if (status === 'processing') return 'Analyse...';
    if (status === 'completed') return 'Relancer le contrôle';
    if (status === 'error') return 'Réessayer';
    return 'Vérifier le devis';
  }

  getBatchDovaResultLabel(draft: any): string {
    const missingCount = Number(draft.get('dovaMissingCount')?.value || 0);
    if (missingCount === 0) return 'Conforme';

    return `${missingCount} point${missingCount > 1 ? 's' : ''} manquant${missingCount > 1 ? 's' : ''}`;
  }

  getBatchDovaResultTooltip(draft: any): string {
    const dovaResponse = this.parseDraftDovaAnalysis(draft.get('dovaAnalysis')?.value);
    const missingFields = this.extractMissingFieldsFromDovaResponse(dovaResponse);

    if (missingFields.length === 0) {
      return 'Aucune anomalie détectée.';
    }

    return missingFields
      .map(field => field.label || field.status)
      .filter(Boolean)
      .join('\n');
  }

  openBatchDraftSavedDovaSummary(index: number): void {
    const draftGroup = this.draftsArray.at(index) as FormGroup;
    const dovaResponse = this.parseDraftDovaAnalysis(draftGroup.get('dovaAnalysis')?.value);

    if (!dovaResponse) {
      this._snackBar.open('Aucun résultat IA disponible pour ce brouillon.', 'Fermer', {
        duration: 3000,
        panelClass: 'warn-snackbar'
      });
      return;
    }

    this.openBatchDraftDovaSummary(draftGroup, dovaResponse);
  }

  private openBatchDraftDovaSummary(draftGroup: FormGroup, dovaResponse: any): void {
    const projectId = draftGroup.get('projectId')?.value;
    const combinedFiles = this.getBatchDraftSummaryFiles(draftGroup);
    const missingFields = this.extractMissingFieldsFromDovaResponse(dovaResponse);
    const submissionContext = this.getSubmissionContext();

    this._uploadSummaryService.setFiles(combinedFiles);
    this._uploadSummaryService.setMissingFields(missingFields);

    this._router.navigate(['/project/documents-upload-summary'], {
      queryParams: { projectId, draftProject: true },
      state: {
        combinedFiles,
        projectForm: {},
        projectStatus: 'Brouillon',
        creationMode: this.creationMode,
        projectId,
        referenceProjet: draftGroup.get('projectReference')?.value || null,
        projectReference: draftGroup.get('projectReference')?.value || null,
        missingFields,
        submissionContext,
        companyId: submissionContext?.companyId,
        partnerId: submissionContext?.partnerId,
        isDraftProject: true,
        draftFileName: draftGroup.get('fileName')?.value || null,
        draftDevisFilePath: draftGroup.get('devisFilePath')?.value || null,
        returnToBatchDraft: true,
        draftProcessedNotification: {
          content: JSON.stringify(dovaResponse)
        } as MessageNotification,
      }
    });
  }

  private getBatchDraftSummaryFiles(draftGroup: FormGroup): { files: File[]; card: string }[] {
    const file = draftGroup.get('file')?.value as File | null;
    return file ? [{ files: [file], card: 'Devis' }] : [];
  }

  private getBatchDraftGroupByProjectId(projectId: string): FormGroup | null {
    if (!projectId) return null;

    const draft = this.draftsArray.controls.find(control =>
      String(control.get('projectId')?.value) === String(projectId)
    );

    return draft ? draft as FormGroup : null;
  }

  isBatchSubmissionValid(): boolean {
    if (this.draftsArray.length === 0) return false;
    for (let i = 0; i < this.draftsArray.length; i++) {
      const group = this.draftsArray.at(i);
      if (group.invalid || group.get('operations')?.value.length === 0) return false;
      if (!group.get('projectId')?.value) return false;
    }
    return true;
  }

  async submitBatchProjects(): Promise<void> {
    if (!this.isBatchSubmissionValid()) return;
    this.isUploading = true;
    const submissionContext = this.getSubmissionContext();
    let successCount = 0;

    for (let i = 0; i < this.draftsArray.length; i++) {
      const draftData = this.draftsArray.at(i).value;
      const formData = new FormData();

      if (draftData.additionalFiles && draftData.additionalFiles.length > 0) {
        draftData.additionalFiles.forEach((file: File, idx: number) => {
          formData.append(`OtherFiles[${idx}].BlobFile`, file);
          formData.append(`OtherFiles[${idx}].Name`, file.name);
        });
      }

      const beneficiaireInfo = {
        dateEngagement: draftData.dateEngagement,
        dateTravauxPrevisionnelle: draftData.dateTravauxPrevisionnelle,
        siret: submissionContext?.siretPartner?.trim()
      };
      formData.append('ProjectFinancementBeneficiaireInfo', JSON.stringify(beneficiaireInfo));

      try {
        await new Promise((resolve, reject) => {
          const requestPayload: any = {
            companyId: submissionContext.companyId,
            partnerId: submissionContext.partnerId,
            userId: submissionContext.userId,
            projectFinancementFilesBody: formData,
            projectFinancementOperations: this.getMappedCeeOperationsBatch(draftData.operations, draftData.showCoupDePouce),
            partnerName: submissionContext.partnerName,
          };

          if (submissionContext.mandateId) {
            requestPayload.mandateId = submissionContext.mandateId;
            requestPayload.mandateName = submissionContext.mandateName;
          }

          this._creationProjetService.submitBatchDraftProjectTera(draftData.projectId, requestPayload).subscribe({
            next: resolve,
            error: reject
          });
        });
        successCount++;
      } catch (error) {
        this._snackBar.open(`Erreur pour le fichier ${draftData.fileName}`, 'Fermer', { duration: 3000, panelClass: 'warn-snackbar' });
      }
    }

    this.isUploading = false;
    if (successCount > 0) {
      this._snackBar.open(`Vos ${successCount} projets ont été validés avec succès !`, 'Fermer', { duration: 4000, panelClass: 'accent-snackbar' });
      this.getProjectHistory();
      this.goToDossiersProjet();
    }
  }

  loadPendingDrafts(): void {
    const submissionContext = this.getSubmissionContext();
    if (!submissionContext) return;

    this._creationProjetService.getPendingDraftProjects(submissionContext.companyId, submissionContext.partnerId).subscribe({
      next: (drafts: DraftProjectFinancementDto[]) => {
        drafts.forEach(draft => {
          const fileName = draft.devisFileName || 'Devis_Brouillon.pdf';

          this.addBatchDraftRow(draft.id, fileName, null, null, draft);
        });
      },
      error: (err) => console.error('Erreur lors du chargement des brouillons', err)
    });
  }

  private handleBatchFiles(files: File[]): void {
    const maxSize = 25 * 1024 * 1024; // 25 Mo
    files.forEach(file => {
      if (file.size > maxSize) {
        this.queueSnackbar(`Le fichier ${file.name} dépasse 25 Mo.`, 'warn-snackbar', 4500);
        return;
      }
      this.uploadAndCreateDraft(file);
    });
  }

  private uploadAndCreateDraft(file: File): void {
    const submissionContext = this.getSubmissionContext();
    if (!submissionContext) return;

    const formData = new FormData();
    const extension = file.name.split('.').pop() || '';
    formData.append('DevisFile.BlobFile', file);
    formData.append('DevisFile.Name', file.name);
    formData.append('DevisFile.ContentType', file.type);
    formData.append('DevisFile.Extension', extension);

    this._creationProjetService.createBatchDraftProject(
      submissionContext.companyId,
      submissionContext.partnerId,
      submissionContext.mandateId,
      submissionContext.partnerName,
      formData,
      submissionContext.userId
    ).subscribe({
      next: (res: any) => {
        const draftProjectId = res?.projectId || res?.ProjectId || res?.id || res?.Id;

        if (!draftProjectId) {
          this.queueSnackbar('Le brouillon a été créé, mais son identifiant est introuvable.', 'warn-snackbar', 3500);
          return;
        }

        this.queueSnackbar(`Brouillon créé avec succès : ${file.name}`, 'accent-snackbar', 2000);
        this.addBatchDraftRow(
          draftProjectId,
          file.name,
          file.size,
          file,
          { projectReference: res?.projectReference || res?.ProjectReference || '' }
        );
      },
      error: () => {
        this.queueSnackbar(`Erreur lors de la création du brouillon pour ${file.name}`, 'warn-snackbar', 4500);
      }
    });
  }

  private addBatchDraftRow(projectId: string | null, fileName: string, fileSize: number | null, fileObj: File | null, existingData?: any): void {
    const benefInfo = existingData?.projectFinancementBeneficiaireInfo;

    const draftGroup = this._fb.group({
      projectId: [projectId],
      projectReference: [existingData?.projectReference || existingData?.referenceProjet || ''],
      file: [fileObj],
      fileName: [fileName],
      devisFilePath: [existingData?.devisFilePath || null],
      fileSize: [fileSize ? this.formatBatchBytes(fileSize) : ''],
      dateTravauxPrevisionnelle: [benefInfo?.dateTravauxPrevisionnelle || '', Validators.required],
      dateEngagement: [benefInfo?.dateEngagement || '', Validators.required],
      availableGroups: [[] as any[]],
      operations: [existingData?.projectFinancementOperations || []],
      operationSearch: [''],
      additionalFiles: [[]],
      showCoupDePouce: [false],
      dovaStatus: [existingData?.dovaAnalysis ? 'completed' : 'idle'],
      dovaProgressPercent: [existingData?.dovaAnalysis ? 100 : null],
      dovaProgressMessage: [existingData?.dovaAnalysis ? 'Analyse déjà disponible.' : null],
      dovaAnalysis: [existingData?.dovaAnalysis || null],
      dovaMissingCount: [existingData?.dovaAnalysis ? this.countDovaMissingFields(this.parseDraftDovaAnalysis(existingData?.dovaAnalysis)) : null]
    });

    draftGroup.get('dateEngagement')?.valueChanges.subscribe(date => {
      if (date) {
        draftGroup.get('operations')?.setValue([] as any[]);
        this.loadGenericOperationsForRow(date, draftGroup);
      } else {
        draftGroup.get('availableGroups')?.setValue([]);
      }
    });

    draftGroup.get('operations')?.valueChanges.subscribe((ops: any[]) => {
      if (!this.hasCdpOperationSelected(ops)) {
        draftGroup.get('showCoupDePouce')?.setValue(false, { emitEvent: false });
      }
    });

    if (benefInfo?.dateEngagement) {
      this.loadGenericOperationsForRow(benefInfo.dateEngagement, draftGroup);
    }

    this.draftsArray.push(draftGroup);
  }

  private getMappedCeeOperationsBatch(operations: any[], showCdp: boolean): any[] {
    return operations.map((op, index) => {
      let fileId = op.id;
      let fileName = '';

      if (op.ceeOperationFiles && op.ceeOperationFiles.length > 0) {
        const matchingFile = op.ceeOperationFiles.find((file: any) =>
          showCdp ? file.isCdp === true : !file.isCdp
        );

        if (matchingFile) {
          fileId = matchingFile.id;
          fileName = matchingFile.name;
        } else {
          fileId = op.ceeOperationFiles[0].id;
          fileName = op.ceeOperationFiles[0].name;
        }
      }
      return {
        genericOperationId: op.id,
        genericOperationName: op.label,
        ceeOperationFileId: fileId,
        ceeOperationFileName: fileName,
        rank: index + 1,
        cdp: showCdp
      };
    });
  }

  private createClassicProject(formData: FormData, submissionContext: any): void {
    this.projectForm.get('nom')?.setValue(formatName(this.projectForm.get('nom')?.value));
    this.projectForm.get('prenom')?.setValue(formatName(this.projectForm.get('prenom')?.value));
    this.projectForm.get('email')?.setValue(this.projectForm.get('email')?.value?.trim());
    this.projectForm.get('tel')?.setValue(this.projectForm.get('tel')?.value?.trim());
    this.projectForm.get('rs')?.setValue(this.projectForm.get('rs')?.value?.trim());
    this.projectForm.get('siret')?.setValue(submissionContext?.siretPartner?.trim());

    const requestPayload: any = {
      companyId: submissionContext.companyId,
      partnerId: submissionContext.partnerId,
      projectFinancementFilesBody: formData,
      projectFinancementOperations: this.getMappedCeeOperations(), // Get operations list
      partnerName: submissionContext.partnerName,
      isImportedSignedDevis: !this.isPreparationMode
    };

    if (submissionContext.mandateId) {
      requestPayload.mandateId = submissionContext.mandateId;
      requestPayload.mandateName = submissionContext.mandateName;
    }

    formData.append('ProjectFinancementBeneficiaireInfo', JSON.stringify(this.projectForm.value));

    const apiCall$ = this.isPreparationMode
      ? this._creationProjetService.createPrepareProject(requestPayload)
      : this._creationProjetService.createClassicProjectFinancementTera(requestPayload);

    apiCall$.subscribe({
      next: (res: any) => {
        this.isUploading = false;
        this.resetAll();
        this.getProjectHistory();

        const successMsg = this.isPreparationMode
          ? `Votre projet a bien été créé en préparation avec la référence : ${res.projectReference}`
          : `Votre projet a bien été créé et envoyé avec la référence : ${res.projectReference}`;

        this._snackBar.open(successMsg, null, {
          duration: 3000,
          panelClass: 'accent-snackbar'
        });

      },
      error: () => {
        this.isUploading = false;
        this._snackBar.open('Erreur lors de la création du projet. Veuillez réessayer', null, {
          duration: 2500,
          panelClass: 'warn-snackbar'
        });
      }
    });
  }

  private createCssIndAfterWorksProject(formData: FormData, submissionContext: any): void {
    this.projectForm.get('nom')?.setValue(formatName(this.projectForm.get('nom')?.value));
    this.projectForm.get('prenom')?.setValue(formatName(this.projectForm.get('prenom')?.value));
    this.projectForm.get('email')?.setValue(this.projectForm.get('email')?.value?.trim());
    this.projectForm.get('tel')?.setValue(this.projectForm.get('tel')?.value?.trim());
    this.projectForm.get('rs')?.setValue(this.projectForm.get('rs')?.value?.trim());
    this.projectForm.get('siret')?.setValue(submissionContext?.siretPartner?.trim());

    formData.append('ProjectFinancementBeneficiaireInfo', JSON.stringify(this.projectForm.value));
    formData.append('CreationMode', 'signed');
    formData.append('IsPreparationProject', 'true');

    const request: CreatePrepareProjectRequest = {
      companyId: submissionContext.companyId,
      partnerId: submissionContext.partnerId,
      projectFinancementFilesBody: formData,
      projectFinancementOperations: this.getMappedCeeOperations(),
      mandateId: submissionContext.mandateId,
      mandateName: submissionContext.mandateName,
      partnerName: submissionContext.partnerName,
      isImportedSignedDevis: true,
    };

    this._creationProjetService.createPrepareProject(request).subscribe({
      next: (res: any) => {
        this.isUploading = false;
        this.resetAll();
        this.getProjectHistory();

        const createdProjectId = res?.projectId || res?.id;

        this._snackBar.open(
          `Votre projet ${res.projectReference} a bien ete cree dans SICA. L'analyse est en cours.`,
          null,
          {
            duration: 3500,
            panelClass: 'accent-snackbar'
          }
        );

        if (!createdProjectId) {
          this._snackBar.open('Le projet a ete cree, mais son identifiant est introuvable.', null, {
            duration: 3000,
            panelClass: 'warn-snackbar'
          });
          this._router.navigate(['/partner-dossier-project']);
          return;
        }

        this._dialog.open(RedirectionPopUpComponent, {
          data: {
            id: createdProjectId,
            waitForDraftProcessing: true,
            refreshAfterProcessing: true
          },
          disableClose: true,
          hasBackdrop: true,
          panelClass: 'redirect-dialog',
          width: '850px'
        });
      },
      error: () => {
        this.isUploading = false;
        this._snackBar.open('Erreur lors de la creation du projet. Veuillez reessayer', null, {
          duration: 2500,
          panelClass: 'warn-snackbar'
        });
      }
    });
  }

  getBatchSubmitTooltip(): string {
    if (this.draftsArray.length === 0) {
      return 'Veuillez ajouter au moins un devis';
    }
    if (this.draftsArray.controls.some(ctrl => !ctrl.get('projectId')?.value)) {
      return 'Veuillez attendre la création du brouillon pour chaque devis';
    }
    if (!this.isBatchSubmissionValid()) {
      return 'Veuillez remplir les dates et au moins une opération pour chaque devis';
    }
    if (this.isUploading) {
      return 'Votre dépôt est en cours, veuillez patienter...';
    }
    if (this.selectedMandataire && !this.selectedPartnerByMandate) {
      return 'Veuillez sélectionner un partenaire';
    }
    return '';
  }

  private queueSnackbar(msg: string, panelClass: string = 'accent-snackbar', duration: number = 4000) {
    this.snackbarQueue.push({ msg, panelClass, duration });
    this.showNextSnackbar();
  }

  private showNextSnackbar() {
    if (this.isSnackbarShowing || this.snackbarQueue.length === 0) return;

    this.isSnackbarShowing = true;
    const current = this.snackbarQueue.shift();

    const ref = this._snackBar.open(current.msg, 'Fermer', {
      duration: current.duration,
      panelClass: current.panelClass
    });

    ref.afterDismissed().subscribe(() => {
      this.isSnackbarShowing = false;
      this.showNextSnackbar(); // Show the next one in line
    });
  }
}
